// Browser-side WebAuthn bridge for the fido2-net-lib backend.
//
// fido2-net-lib emits/consumes JSON where binary fields (challenge, user.id,
// credential ids, attestation/assertion blobs) are base64url strings. The
// browser's navigator.credentials API works in ArrayBuffers. These helpers
// convert between the two and shape the response exactly as the backend expects
// (AuthenticatorAttestationRawResponse / AuthenticatorAssertionRawResponse).

export function passkeysSupported(): boolean {
    return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

function b64urlToBuf(value: string): ArrayBuffer {
    const pad = value.length % 4 === 0 ? "" : "=".repeat(4 - (value.length % 4));
    const b64 = (value + pad).replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
}

function bufToB64url(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

interface CredDescriptor { type: PublicKeyCredentialType; id: string; transports?: AuthenticatorTransport[] }

// The server options JSON is an opaque fido2-net-lib structure; we only touch
// the binary fields, so it is intentionally loosely typed.
type ServerOptions = Record<string, unknown> & {
    challenge: string;
    user?: { id: string; name: string; displayName: string };
    excludeCredentials?: CredDescriptor[];
    allowCredentials?: CredDescriptor[];
};

/** Run the registration ceremony; returns the raw response the backend expects. */
export async function createPasskey(options: ServerOptions): Promise<unknown> {
    const publicKey: PublicKeyCredentialCreationOptions = {
        ...(options as unknown as PublicKeyCredentialCreationOptions),
        challenge: b64urlToBuf(options.challenge),
        user: {
            ...(options.user as { name: string; displayName: string }),
            id: b64urlToBuf(options.user!.id)
        },
        excludeCredentials: (options.excludeCredentials ?? []).map((c) => ({
            type: c.type,
            id: b64urlToBuf(c.id),
            transports: c.transports
        }))
    };

    const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
    if (!cred) throw new Error("No passkey was created.");
    const resp = cred.response as AuthenticatorAttestationResponse;
    return {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        extensions: cred.getClientExtensionResults(),
        response: {
            attestationObject: bufToB64url(resp.attestationObject),
            clientDataJSON: bufToB64url(resp.clientDataJSON)
        }
    };
}

/** Run the assertion (login) ceremony; returns the raw response the backend expects. */
export async function getPasskeyAssertion(options: ServerOptions): Promise<unknown> {
    const publicKey: PublicKeyCredentialRequestOptions = {
        ...(options as unknown as PublicKeyCredentialRequestOptions),
        challenge: b64urlToBuf(options.challenge),
        allowCredentials: (options.allowCredentials ?? []).map((c) => ({
            type: c.type,
            id: b64urlToBuf(c.id),
            transports: c.transports
        }))
    };

    const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
    if (!cred) throw new Error("No passkey was selected.");
    const resp = cred.response as AuthenticatorAssertionResponse;
    return {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        extensions: cred.getClientExtensionResults(),
        response: {
            authenticatorData: bufToB64url(resp.authenticatorData),
            signature: bufToB64url(resp.signature),
            clientDataJSON: bufToB64url(resp.clientDataJSON),
            userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null
        }
    };
}
